import { useEffect, useState } from 'react'
import { Avatar, Button, FileButton, Group, Modal, Stack, Text } from '@mantine/core'
import { useMedplum } from '@medplum/react'
import { normalizeErrorString } from '@medplum/core'
import type { PractitionerPhoto } from '@behuman/core-contract'
import { PHOTO_TYPES, photoProblem } from '@/utils/practitionerPhoto'

type ProfilePhotoModalProps = {
  userName: string
  /** Whether a photo is stored, so the buttons read Replace/Remove rather than Add. */
  hasPhoto: boolean
  /** The stored photo as something an `<img>` can show; undefined while none or not yet loaded. */
  photoUrl?: string
  onClose: () => void
  /** Writes the photo — `null` removes it. A rejection keeps the modal open with the pick. */
  onSave: (photo: PractitionerPhoto | null) => Promise<unknown>
}

/**
 * Upload, replace or remove the signed-in member's profile photo, from My Profile's summary
 * card. A picked file becomes a Binary through the viewer's own Medplum client, as every
 * console upload does, and its canonical `Binary/<id>` reference is what `onSave` writes
 * (the my-profile write, on the admin client).
 */
export function ProfilePhotoModal({
  userName,
  hasPhoto,
  photoUrl,
  onClose,
  onSave,
}: ProfilePhotoModalProps) {
  const medplum = useMedplum()
  const [file, setFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  // Object URLs are leaked unless revoked once the preview is replaced or the
  // modal closes.
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null)
      return
    }
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  const handlePick = (picked: File | null) => {
    if (!picked) return
    const validationError = photoProblem(picked)
    if (validationError) {
      setError(validationError)
      setFile(null)
      return
    }
    setError(null)
    setFile(picked)
  }

  const savePhoto = async (next: 'upload' | 'remove') => {
    setIsSaving(true)
    setError(null)
    try {
      let photo: PractitionerPhoto | null = null
      if (next === 'upload' && file) {
        const binary = await medplum.createBinary({
          data: file,
          contentType: file.type,
          filename: file.name,
        })
        // The picker already checked the type, so it is one of the three the contract takes.
        photo = {
          url: `Binary/${binary.id}`,
          contentType: file.type as PractitionerPhoto['contentType'],
          title: file.name,
        }
      }
      await onSave(photo)
      onClose()
    } catch (err) {
      setError(normalizeErrorString(err) || 'Failed to save the profile photo.')
    } finally {
      setIsSaving(false)
    }
  }

  const shownUrl = previewUrl ?? photoUrl

  return (
    <Modal opened onClose={onClose} title="Profile photo" centered size="sm" withinPortal>
      <Stack gap="md" align="center">
        <Avatar src={shownUrl} name={userName} color="#275587" size={120} radius={9999} />

        <Text size="sm" c="dimmed" ta="center">
          JPEG, PNG or WebP, up to 5&nbsp;MB. The image is cropped to a circle.
        </Text>

        {error && (
          <Text size="sm" c="red" ta="center" role="alert">
            {error}
          </Text>
        )}

        <Group gap="sm" justify="center">
          <FileButton
            onChange={handlePick}
            accept={PHOTO_TYPES.join(',')}
            inputProps={{ 'aria-label': 'Photo file' }}
          >
            {(props) => (
              <Button {...props} variant="light" disabled={isSaving}>
                {hasPhoto ? 'Choose new photo' : 'Choose photo'}
              </Button>
            )}
          </FileButton>
          {hasPhoto && (
            <Button
              variant="subtle"
              color="red"
              loading={isSaving && !file}
              disabled={isSaving}
              onClick={() => void savePhoto('remove')}
            >
              Remove photo
            </Button>
          )}
        </Group>

        <Group gap="sm" justify="flex-end" className="w-full">
          <Button variant="default" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            disabled={!file || isSaving}
            loading={isSaving && !!file}
            onClick={() => void savePhoto('upload')}
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
